import { useCallback, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { LoginMfaStep } from "@/components/mfa/LoginMfaStep";
import { login } from "@/lib/api";
import { safeNextPath } from "@/lib/login-redirect";
import type { MfaChallenge } from "@/lib/mfa-api";
import { AuthLogo } from "./AuthLogo";
import { type PasskeyAvailability, PasskeySignIn } from "./PasskeySignIn";

export function LoginPage() {
	const location = useLocation();
	const [params] = useSearchParams();
	const [banner, setBanner] = useState(
		(location.state as { message?: string } | null)?.message ?? null,
	);
	const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [passkeyAvailability, setPasskeyAvailability] = useState<PasskeyAvailability>("checking");

	async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
		e.preventDefault();
		setLoading(true);
		setError(null);
		try {
			const challenge = await login({ email, password });
			if (challenge) {
				setBanner(null);
				setMfaChallenge(challenge);
			} else {
				goToNext();
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "予期しないエラーが発生しました");
		} finally {
			setLoading(false);
		}
	}

	const goToNext = useCallback(() => {
		window.location.replace(safeNextPath(params.get("next"), window.location.origin));
	}, [params]);

	function restartLogin(message: string) {
		setMfaChallenge(null);
		setPassword("");
		setError(message);
	}

	return (
		<div
			style={{
				minHeight: "100vh",
				background: "var(--surface-2)",
				display: "grid",
				placeItems: "center",
				fontFamily: "var(--font-sans)",
			}}
		>
			<div
				style={{
					width: "100%",
					maxWidth: mfaChallenge ? 440 : 360,
					background: "var(--surface-1)",
					borderRadius: "var(--radius-5)",
					boxShadow: "var(--shadow-card)",
					padding: "32px",
					display: "flex",
					flexDirection: "column",
					gap: 24,
				}}
			>
				<header style={{ display: "flex", flexDirection: "column", gap: 4 }}>
					<AuthLogo />
					<h1
						style={{
							margin: 0,
							fontSize: "var(--text-display)",
							fontWeight: 600,
							lineHeight: "var(--leading-snug)",
							letterSpacing: "var(--tracking-display)",
							color: "var(--fg-1)",
						}}
					>
						{mfaChallenge ? "本人確認" : "サインイン"}
					</h1>
					<p
						style={{
							margin: 0,
							fontSize: "var(--text-ui)",
							color: "var(--fg-2)",
							lineHeight: "var(--leading-ui)",
						}}
					>
						{mfaChallenge ? "多要素認証" : "ワークスペースにアクセス"}
					</p>
				</header>

				{banner && (
					<div
						style={{
							padding: "8px 10px",
							borderRadius: "var(--radius-3)",
							background: "var(--success-subtle)",
							boxShadow: "0 0 0 1px var(--success-line)",
							fontSize: "var(--text-caption)",
							color: "var(--success)",
							lineHeight: "var(--leading-ui)",
						}}
					>
						{banner}
					</div>
				)}

				{mfaChallenge ? (
					<LoginMfaStep challenge={mfaChallenge} onSignedIn={goToNext} onRestart={restartLogin} />
				) : (
					<form
						onSubmit={handleSubmit}
						style={{ display: "flex", flexDirection: "column", gap: 12 }}
					>
						<PasskeySignIn
							disabled={loading}
							onSignedIn={goToNext}
							onError={setError}
							onAvailabilityChange={setPasskeyAvailability}
						/>

						<label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								style={{
									fontSize: "var(--text-caption)",
									fontWeight: 500,
									color: "var(--fg-2)",
									letterSpacing: "var(--tracking-wide)",
								}}
							>
								メールアドレス
							</span>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="you@company.com"
								required
								autoComplete="username webauthn"
								style={{
									height: 36,
									padding: "0 10px",
									fontSize: "var(--text-ui)",
									color: "var(--fg-1)",
									background: "var(--surface-1)",
									border: "none",
									borderRadius: "var(--radius-3)",
									boxShadow: "var(--shadow-ring)",
									outline: "none",
									fontFamily: "var(--font-sans)",
									transition: "box-shadow 150ms ease",
								}}
								onFocus={(e) => {
									e.currentTarget.style.boxShadow = "var(--shadow-focus)";
								}}
								onBlur={(e) => {
									e.currentTarget.style.boxShadow = "var(--shadow-ring)";
								}}
							/>
						</label>

						<label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								style={{
									fontSize: "var(--text-caption)",
									fontWeight: 500,
									color: "var(--fg-2)",
									letterSpacing: "var(--tracking-wide)",
								}}
							>
								パスワード
							</span>
							<input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="••••••••"
								required
								autoComplete="current-password"
								style={{
									height: 36,
									padding: "0 10px",
									fontSize: "var(--text-ui)",
									color: "var(--fg-1)",
									background: "var(--surface-1)",
									border: "none",
									borderRadius: "var(--radius-3)",
									boxShadow: "var(--shadow-ring)",
									outline: "none",
									fontFamily: "var(--font-sans)",
									transition: "box-shadow 150ms ease",
								}}
								onFocus={(e) => {
									e.currentTarget.style.boxShadow = "var(--shadow-focus)";
								}}
								onBlur={(e) => {
									e.currentTarget.style.boxShadow = "var(--shadow-ring)";
								}}
							/>
						</label>

						<Link
							to="/forgot-password"
							style={{
								alignSelf: "flex-end",
								marginTop: -4,
								fontSize: "var(--text-caption)",
								color: "var(--accent)",
								textDecoration: "none",
							}}
						>
							パスワードをお忘れですか？
						</Link>

						{error && (
							<div
								style={{
									padding: "8px 10px",
									borderRadius: "var(--radius-3)",
									background: "var(--danger-subtle)",
									boxShadow: "0 0 0 1px var(--danger-line)",
									fontSize: "var(--text-caption)",
									color: "var(--danger)",
									lineHeight: "var(--leading-ui)",
								}}
							>
								{error}
							</div>
						)}

						<ConfirmButton
							type="submit"
							label="サインイン"
							variant={passkeyAvailability === "unusable" ? "primary" : "secondary"}
							loading={loading}
						/>
					</form>
				)}
			</div>
		</div>
	);
}
